import { useCallback, useEffect, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import { ResultStep } from '@/common/components/ConnectionSteps/ResultStep'
import { getApplicationToken } from '@/common/hooks/useApplicationAuth'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { useModalApplication } from '@/common/hooks/useModalApplication'
import { AuthResult } from '@/common/models/applicationConnection'
import { ApiError } from '@/lib/axios'
import { useDisconnectApplicationAccountUsersAuth } from '../pages/ProfileAccess/hooks/useDisconnectApplicationAccountUsersAuth'
import { ValidationStep } from './ChangePasswordSteps/ValidationStep'

type FlowState = 'validation' | 'result'

export function useAccessDisconnectManager() {
  const { t } = useTranslation(['common', 'profile'])
  const { handleModal, updateModal, resetModal } = useModalApplication()
  const { userInfo } = useGetUserInfo()
  const { handleDisconnectApplicationAccountUsersAuth } =
    useDisconnectApplicationAccountUsersAuth()

  const [currentState, setCurrentState] = useState<FlowState>('validation')
  const [authResult, setAuthResult] = useState<AuthResult>({
    success: false,
    loading: false,
  })
  const [authError, setAuthError] = useState<boolean>(false)
  const [friendlyMessage, setFriendlyMessage] = useState<string | null>(null)
  const [appData, setAppData] = useState<{
    applicationAccountUsersAuthId: string
    applicationName: string
    accountName: string
  } | null>(null)
  const [validationValid, setValidationValid] = useState(false)

  const validationFormRef = useRef<{
    serviceSolutionsPassword?: string
  } | null>(null)

  const renderStep = useCallback(() => {
    if (!appData) return null

    const stepNumber = currentState === 'validation' ? 1 : 2

    switch (currentState) {
      case 'validation':
        return (
          <ValidationStep
            appData={{
              applicationAccountUserId: '',
              applicationName: appData.applicationName,
              accountName: appData.accountName,
            }}
            stepInfo={{ current: stepNumber, total: 2 }}
            onValidationChange={setValidationValid}
            onFormChange={(values) => {
              validationFormRef.current =
                values as typeof validationFormRef.current
            }}
            context="disconnect"
          />
        )
      case 'result':
        return (
          <ResultStep
            appData={{
              applicationId: '',
              applicationName: appData.applicationName,
              userId: '',
            }}
            authResult={authResult}
            stepInfo={{ current: stepNumber, total: 2 }}
            authError={authError}
            namespace="disconnect"
            accountName={appData.accountName}
            friendlyMessage={friendlyMessage}
          />
        )
      default:
        return null
    }
  }, [appData, currentState, authResult, authError, friendlyMessage])

  const handleNext = useCallback(async () => {
    if (!appData) return

    switch (currentState) {
      case 'validation': {
        const currentFormValues = validationFormRef.current
        if (!validationValid || !currentFormValues?.serviceSolutionsPassword) {
          return
        }

        setAuthResult({ success: false, loading: true })
        setCurrentState('result')

        try {
          await getApplicationToken({
            username: userInfo?.Person?.email || '',
            password: currentFormValues.serviceSolutionsPassword,
          })

          await handleDisconnectApplicationAccountUsersAuth(
            appData.applicationAccountUsersAuthId,
          )

          setAuthResult({ success: true, loading: false })

          setTimeout(() => {
            resetModal()
          }, 3000)
        } catch (error) {
          setAuthResult({ success: false, loading: false })
          setAuthError(true)
          if (error instanceof AxiosError) {
            const apiError = error.response?.data as ApiError
            setFriendlyMessage(apiError?.friendlyMessage || null)
          }
        }
        break
      }

      case 'result': {
        if (authError) {
          setCurrentState('validation')
          setAuthError(false)
          setAuthResult({ success: false, loading: false })
          setAppData(null)
          setValidationValid(false)
          resetModal()
        } else {
          setCurrentState('validation')
          setAuthResult({ success: false, loading: false })
          setAppData(null)
          setValidationValid(false)
          resetModal()
        }
        return
      }
    }
  }, [
    appData,
    currentState,
    validationValid,
    userInfo?.Person?.email,
    resetModal,
    authError,
    handleDisconnectApplicationAccountUsersAuth,
  ])

  const getCancelButtonText = useCallback(() => {
    if (currentState === 'result' && authError)
      return t('profile:actions.back_button.text')
    return undefined
  }, [currentState, authError, t])

  const handleBack = useCallback(() => {
    if (currentState === 'result' && authError) {
      setCurrentState('validation')
      setAuthError(false)
      setAuthResult({ success: false, loading: false })
      setFriendlyMessage(null)
    }
  }, [currentState, authError])

  useEffect(() => {
    if (!appData) return

    const getButtonText = () => {
      if (currentState === 'validation')
        return t('profile:actions.disconnect_button.text')
      if (currentState === 'result' && authError)
        return t('profile:actions.close_button.text')
      return t('profile:actions.close_button.text')
    }

    const shouldShowButtons =
      currentState === 'result' ? !authResult.loading : true

    const isDisabled = currentState === 'validation' ? !validationValid : false

    updateModal({
      title: t('profile:access.modal.disconnect.title'),
      description: renderStep(),
      confirmText: getButtonText(),
      cancelText: getCancelButtonText(),
      showButtons: shouldShowButtons,
      onConfirm: handleNext,
      onCancel: getCancelButtonText() ? handleBack : undefined,
      disabled: isDisabled,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentState,
    authResult,
    appData,
    validationValid,
    authError,
    t,
    getCancelButtonText,
    handleBack,
  ])

  const handleDisconnectApplication = useCallback(
    async (
      applicationAccountUsersAuthId: string,
      applicationName: string,
      accountName: string,
    ) => {
      setAppData({
        applicationAccountUsersAuthId,
        applicationName,
        accountName,
      })
      setCurrentState('validation')
      setValidationValid(false)
      setAuthError(false)
      setFriendlyMessage(null)

      handleModal({
        title: t('profile:access.modal.disconnect.title'),
        description: renderStep(),
        showButtons: true,
        onConfirm: handleNext,
      })
    },
    [handleModal, renderStep, handleNext, t],
  )

  return {
    handleDisconnectApplication,
  }
}
